/**
 * Perspective Rating Widget
 * 
 * Shows three rating widgets for rating kinks from different perspectives:
 * - Dom: Rating when dominating/topping
 * - Sub: Rating when submitting/bottoming
 * - Fantasy Only: Interest in fantasy/imagination only, not practice
 * 
 * Uses the existing RatingWidget component internally.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Heart, Sparkles, Loader2, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RatingWidget from './RatingWidget';
import { API_BASE } from '../utils/api';

const PERSPECTIVES = [
    {
        key: 'dom',
        label: 'Dom / Giver',
        description: 'When dominating, topping, or giving',
        icon: Crown
    },
    {
        key: 'sub',
        label: 'Sub / Receiver',
        description: 'When submitting, bottoming, or receiving',
        icon: Heart
    },
    {
        key: 'fantasy_only',
        label: 'Fantasy Only',
        description: 'Interested but only in fantasy',
        icon: Sparkles
    }
];

function PerspectiveRatingWidget({
    entityType,
    entityId,
    showModifiers = false,
    size = 'sm',
    variant = 'compact',
    onRatingChange
}) {
    const { activePersona, getAuthHeaders } = useAuth();

    const [perspectiveRatings, setPerspectiveRatings] = useState({});
    const [loading, setLoading] = useState(false);

    // Fetch all perspective ratings on mount
    useEffect(() => {
        const fetchAllRatings = async () => {
            if (!activePersona || !entityType || !entityId) return;

            setLoading(true);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(
                    `${API_BASE}/api/ratings/${activePersona.id}/${entityType}/${entityId}?all=true`,
                    { headers }
                );

                if (response.ok) {
                    const data = await response.json();
                    // Convert array to map by perspective
                    const ratingsMap = {};
                    for (const r of (data.ratings || [])) {
                        const key = r.perspective || 'general';
                        ratingsMap[key] = r.rating;
                    }
                    setPerspectiveRatings(ratingsMap);
                }
            } catch (err) {
                console.debug('Failed to fetch perspective ratings');
            } finally {
                setLoading(false);
            }
        };

        fetchAllRatings();
    }, [activePersona, entityType, entityId, getAuthHeaders]);

    const handlePerspectiveRating = (perspective, rating) => {
        setPerspectiveRatings(prev => ({
            ...prev,
            [perspective]: rating
        }));

        if (onRatingChange) {
            onRatingChange(perspective, rating);
        }
    };

    const ratedCount = Object.values(perspectiveRatings).filter(r => r !== null && r !== undefined).length;

    if (!activePersona) {
        return (
            <div className="p-4 rounded-lg border border-wine-700/30 bg-dark-800/50">
                <Link
                    to="/login"
                    className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-wine-600 hover:bg-wine-500 text-white font-medium transition-colors"
                >
                    <LogIn size={18} />
                    <span>Sign in to track & rate this kink</span>
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {loading && (
                <div className="flex items-center gap-2 text-sm text-dark-400">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Loading ratings...</span>
                </div>
            )}
            {PERSPECTIVES.map((p) => {
                const Icon = p.icon;
                return (
                    <div
                        key={p.key}
                        className="p-3 rounded-lg border border-wine-700/30 bg-dark-800/50"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Icon size={16} className="text-wine-400" />
                                <span className="text-sm font-medium text-cream">
                                    {p.label}
                                </span>
                            </div>
                            <span className="text-xs text-dark-400">
                                {p.description}
                            </span>
                        </div>
                        <RatingWidget
                            entityType={entityType}
                            entityId={entityId}
                            perspective={p.key}
                            initialRating={perspectiveRatings[p.key] ?? null}
                            size={size}
                            variant={variant}
                            showLabels={true}
                            autoFetch={false}
                            onRatingChange={(rating) => handlePerspectiveRating(p.key, rating)}
                        />
                    </div>
                );
            })}
        </div>
    );
}

export default PerspectiveRatingWidget;
